import { useContext, useEffect } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import { ParentMessageContext } from 'assets/js/pages/engage/workflows/components/ParentMessageProvider';
import { MessageSelectFormValues } from 'assets/js/pages/engage/workflows/components/types';
import * as Form from 'components/form';
import { BranchConditionEnum, WorkflowActionTypeEnum } from 'constants/workflows';

const anyMessageItem = {
    id: 'any',
    label: 'Any message',
};

const defaultMessageSelectValue = [anyMessageItem.id];

interface MessageSelectProps {
    allowMultiple?: boolean;
    condition: BranchConditionEnum;
}

const MessageSelect = ({ condition, allowMultiple = true }: MessageSelectProps) => {
    const { control, setValue, watch, path } =
        Form.useGivebutterFormContext<MessageSelectFormValues>();
    const messages: string[] | null = watch('value.messages');

    const { messageOptions } = useContext(ParentMessageContext);
    const filteredMessageOptions = messageOptions.filter(messageOption => {
        return condition === BranchConditionEnum.MESSAGES_OPEN
            ? messageOption.type !== WorkflowActionTypeEnum.SEND_SMS
            : true;
    });

    useEffect(() => {
        if (!allowMultiple || messages?.length > 0) return;
        setValue('value.messages', defaultMessageSelectValue);
    }, [messages, setValue, allowMultiple]);

    return (
        <Form.Container>
            <Form.Label name={path('value.messages')}>Select the message</Form.Label>
            <Form.Combobox
                control={control}
                name={path('value.messages')}
                allowMultiple={allowMultiple}
            >
                <Combobox.Trigger aria-label="Select message">
                    <Combobox.Select placeholder="Select message" />
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search />
                    <Combobox.Items>
                        {allowMultiple && (
                            <Combobox.Item
                                value={anyMessageItem.id}
                                onSelectItem={({ event }) => {
                                    event.preventDefault();
                                    setValue('value.messages', defaultMessageSelectValue, {
                                        shouldDirty: true,
                                    });
                                }}
                            >
                                <Combobox.CheckboxIndicator />
                                <Combobox.ItemText>
                                    <p className="font-medium">Any message</p>
                                </Combobox.ItemText>
                            </Combobox.Item>
                        )}
                        {filteredMessageOptions.map(option => (
                            <Combobox.Item
                                key={option.id}
                                value={option.id}
                                onSelectItem={({ event, selectedItems, selectedItem }) => {
                                    event.preventDefault();
                                    const selectAnyMessage =
                                        selectedItem === anyMessageItem.id ||
                                        selectedItems.length === 0;

                                    const newValue = selectAnyMessage
                                        ? defaultMessageSelectValue
                                        : selectedItems.filter(v => v !== anyMessageItem.id);

                                    setValue('value.messages', newValue, {
                                        shouldDirty: true,
                                    });
                                }}
                            >
                                {allowMultiple && <Combobox.CheckboxIndicator />}
                                <Combobox.ItemText>
                                    <p className="font-medium">{option.label}</p>
                                </Combobox.ItemText>
                                {!allowMultiple && <Combobox.CheckmarkIndicator />}
                            </Combobox.Item>
                        ))}
                    </Combobox.Items>
                </Combobox.Popover>
            </Form.Combobox>
            <Form.Error name={path('value.messages')} />
        </Form.Container>
    );
};

export { MessageSelect, anyMessageItem, defaultMessageSelectValue };
